// middleware/session-guard.global.ts
import {
  useAuth,
  LOGIN_PATH,
  SUSPENDED_PATH,
  isAuthRoute,
  isPublicSuspensionRoute,
} from '../composables/useAuth'
import { useAppLink } from '@S/appLink'

export default defineNuxtRouteMiddleware(async (to) => {
  const link = useAppLink()
  const { sessionCheck, hydrateFromSession, clearSession, authToken } = useAuth()

  if (link.authVersion !== null && link.tokenAv !== null && link.authVersion !== link.tokenAv) {
    clearSession()
    if (to.meta.layout === 'secure' && !isAuthRoute(to.path) && !isPublicSuspensionRoute(to.path)) {
      return navigateTo(LOGIN_PATH)
    }
    return
  }

  // Helper to redirect safely (no history spam, no self-redirect loops)
  const safeRedirect = (path: string) => {
    if (to.path !== path) {
      return navigateTo(path, { replace: true })
    }
  }

  // 1) No token → block secure pages, allow public/auth/suspension pages
  if (!authToken.value) {
    if (to.meta.layout === 'secure' && !isAuthRoute(to.path) && !isPublicSuspensionRoute(to.path)) {
      return safeRedirect(LOGIN_PATH)
    }
    return
  }

  // 2) With token → validate session BEFORE entering the route
  try {
    const res = await sessionCheck()

    // Invalid/stale token (backend replied with statusCode)
    if (!hydrateFromSession(res)) {
      // Allow auth routes and public pages, but block secure pages
      if (to.meta.layout === 'secure' && !isAuthRoute(to.path)) {
        return safeRedirect(LOGIN_PATH)
      }
      return
    }

    // 3) Suspended users → force to /suspended unless already on /suspended or /contact
    const isSuspended = (res as any).isSuspended === true
    if (isSuspended && !isPublicSuspensionRoute(to.path)) {
      return safeRedirect(SUSPENDED_PATH)
    }

    // 4) Already authenticated → keep login/register clean
    if (!isSuspended && isAuthRoute(to.path)) {
      return safeRedirect('/dashboard')
    }

    // 5) Otherwise proceed
    return
  } catch (err) {
    // Network/unknown error → treat as unauthenticated for secure pages
    clearSession()
    if (to.meta.layout === 'secure' && !isAuthRoute(to.path)) {
      return safeRedirect(LOGIN_PATH)
    }
    return
  }
})
